/**
 * The shared movie/series browser: category rail, poster grid, sort, search.
 *
 * Movies and series differ only in which detail route a tile opens, so they run
 * through one component — a second copy would drift on the fiddly parts
 * (cursor paging, stale responses, scroll reset) rather than on anything users
 * can see.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { CategoryDto, Kind, StreamDto } from '@sflx/shared';
import { useDebounced } from '../hooks/useDebounced';
import { useIngestProgress } from '../hooks/useIngestProgress';
import { useStreamFeed } from '../hooks/useStreamFeed';
import { IngestingNotice } from './IngestBanner';
import { catalog, type StreamQueryParams } from '../lib/api';
import { useAccounts } from '../lib/accounts-store';
import { formatCount } from '../lib/format';
import { Link, navigate } from '../router/router';
import { EmptyState, ErrorNotice, Loading, Spinner } from './Feedback';
import { Icon } from './Icon';
import { Poster } from './Poster';

type Sort = 'name' | 'added' | 'rating';

const SORTS: { value: Sort; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'added', label: 'Recently added' },
  { value: 'rating', label: 'Rating' },
];

interface CatalogBrowserProps {
  kind: Extract<Kind, 'movie' | 'series'>;
  title: string;
  /** Where a tile leads. */
  hrefFor: (accountId: string, stream: StreamDto) => string;
}

export function CatalogBrowser({ kind, title, hrefFor }: CatalogBrowserProps) {
  const { active, loading: accountsLoading } = useAccounts();
  const accountId = active?.id ?? null;

  const [categories, setCategories] = useState<CategoryDto[] | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort>('name');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [step, setStep] = useState<'side' | 'content'>('content');
  const [categoriesVersion, setCategoriesVersion] = useState(0);

  const debouncedSearch = useDebounced(search, 250);

  // Clearing the rail belongs to a change of account or kind. A reload — which
  // also happens when an ingest settles — must not blank the list or drop the
  // category the user is reading.
  useEffect(() => {
    setCategories(null);
    setCategoryId(null);
  }, [accountId, kind]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    catalog
      .categories(accountId, kind)
      .then((result) => {
        if (!cancelled) setCategories(result.categories);
      })
      .catch(() => {
        // The grid still works without a category rail, so a failure here
        // degrades to "everything" rather than blocking the screen.
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, kind, categoriesVersion]);

  const query = useMemo<StreamQueryParams | null>(
    () =>
      accountId
        ? {
            kind,
            category: categoryId,
            q: debouncedSearch.trim() || null,
            sort,
            favorites: favoritesOnly,
            limit: 60,
          }
        : null,
    [accountId, kind, categoryId, debouncedSearch, sort, favoritesOnly],
  );

  const feed = useStreamFeed(accountId, query);
  const { items, loading, loadingMore, error, hasMore, loadMore, total } = feed;

  // A catalog that is still being fetched is not an empty one.
  const ingest = useIngestProgress(accountId, () => {
    setCategoriesVersion((value) => value + 1);
    feed.reload();
  });
  const job = ingest.jobs.find(
    (entry) => entry.kind === kind && (entry.status === 'queued' || entry.status === 'running'),
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Reset the scroll position whenever the result set changes identity.
  const signature = `${categoryId ?? ''}|${debouncedSearch}|${sort}|${favoritesOnly}`;
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [signature]);

  // On mobile the category rail and the results are two separate steps, but
  // search has to work from either one — typing a query is itself a request
  // to see results, so it jumps there rather than leaving the rail up.
  useEffect(() => {
    if (search.trim()) setStep('content');
  }, [search]);

  // Infinite scroll. `rootMargin` fires the fetch a screen early so the grid
  // fills without the user ever seeing the bottom edge.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { root, rootMargin: '600px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, items.length, hasMore]);

  if (accountsLoading && !active) {
    return (
      <div className="page">
        <Loading />
      </div>
    );
  }

  if (!active) {
    return (
      <div className="page">
        <EmptyState
          title="No account selected"
          hint="Add an Xtream account and tick this catalog to fill the screen."
          action={
            <Link className="btn btn--primary" to="/accounts">
              Go to accounts
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="page page--flush">
      {/* Lives outside the mobile step toggle below so it stays on screen
          whether the rail or the results are showing. */}
      <div className="browse__toolbar">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setStep((value) => (value === 'side' ? 'content' : 'side'))}
        >
          <Icon name="sliders" size={15} />
          Categories
        </button>

        <label className="row" style={{ gap: 'var(--s-3)', flex: '1 1 180px', flexWrap: 'nowrap' }}>
          <Icon name="search" size={16} />
          <span className="sr-only">Search {title}</span>
          <input
            className="input"
            type="search"
            placeholder={`Search ${title.toLowerCase()}`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>

        <label className="row" style={{ gap: 'var(--s-2)' }}>
          <span className="sr-only">Sort by</span>
          <select
            className="select"
            style={{ width: 'auto' }}
            value={sort}
            onChange={(event) => setSort(event.target.value as Sort)}
          >
            {SORTS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="chip"
          aria-pressed={favoritesOnly}
          onClick={() => setFavoritesOnly((value) => !value)}
        >
          <Icon name={favoritesOnly ? 'starFilled' : 'star'} size={14} />
          Favorites
        </button>
      </div>

      <div className="browse" data-step={step}>
        <aside className="pane browse__side" aria-label="Categories">
          <div className="pane__head">
            <span className="pane__title">{title}</span>
          </div>
          <div className="pane__body">
            <button
              type="button"
              className="list-row"
              aria-selected={categoryId === null}
              onClick={() => {
                setCategoryId(null);
                setStep('content');
              }}
            >
              <span className="list-row__label">All</span>
              <span className="list-row__count">{total === null ? '' : formatCount(total)}</span>
            </button>
            {(categories ?? []).map((category) => (
              <button
                key={category.id}
                type="button"
                className="list-row"
                aria-selected={categoryId === category.id}
                onClick={() => {
                  setCategoryId(category.id);
                  setStep('content');
                }}
              >
                <span className="list-row__label">{category.name}</span>
                <span className="list-row__count">{formatCount(category.streamCount)}</span>
              </button>
            ))}
            {categories === null ? (
              <div style={{ padding: 'var(--s-5)' }}>
                <Spinner label="Loading" />
              </div>
            ) : null}
          </div>
        </aside>

        <div className="browse__main">
          <div className="browse__scroll" ref={scrollRef}>
            <ErrorNotice error={error} />

            {loading ? (
              <Loading label={`Loading ${title.toLowerCase()}`} />
            ) : !items.length && job && !search ? (
              // Still arriving. Telling the user to "refresh the account" here
              // would start a second, redundant ingest.
              <IngestingNotice job={job} />
            ) : !items.length ? (
              <EmptyState
                title={search ? `Nothing matches "${search}"` : `No ${title.toLowerCase()} here`}
                hint={
                  search
                    ? 'Search runs on the server across the whole catalog, so a different spelling is worth a try.'
                    : 'Try another category, or refresh the account.'
                }
              />
            ) : (
              <>
                <div className="poster-grid">
                  {items.map((stream) => (
                    <Poster
                      key={stream.id}
                      name={stream.name}
                      imageUrl={stream.logoUrl}
                      subtitle={subtitleFor(stream)}
                      corner={stream.favorite ? <Icon name="starFilled" size={12} /> : undefined}
                      positionS={stream.positionS}
                      durationS={stream.durationS}
                      onClick={() => navigate(hrefFor(active.id, stream))}
                    />
                  ))}
                </div>
                <div ref={sentinelRef} style={{ height: 1 }} />
                {loadingMore ? (
                  <div style={{ padding: 'var(--s-5)' }}>
                    <Spinner label="Loading more" />
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function subtitleFor(stream: StreamDto): string | undefined {
  const parts: string[] = [];
  if (stream.year) parts.push(String(stream.year));
  if (stream.rating) parts.push(`${stream.rating.toFixed(1)}`);
  return parts.length ? parts.join(' · ') : undefined;
}
